import { useMutation } from "@apollo/client";
import type { Reference } from "@apollo/client/utilities";
import { LIKE_STORE, CANCEL_LIKE } from "../../../api/store-mutations";
import { NEW_LIKE_FRAGMENT } from "../api/home-fragments";
import type {
  LikeStoreMutation,
  CancelLikeMutation,
} from "../../../__generated__/graphql";

/**
 * likeStore를 하면 캐시에 like 객체를 추가하고,
 * cancelLike를 하면 해당 객체를 삭제합니다.
 */
export function useStoreLikeMutations() {
  const [likeStore] = useMutation(LIKE_STORE, {
    update: (cache, result) => {
      const likeStoreData = result.data?.likeStore as
        | LikeStoreMutation["likeStore"]
        | undefined;
      if (!likeStoreData) return;
      cache.modify({
        id: `Store:${likeStoreData.storeId}`,
        fields: {
          isLiked() {
            return true;
          },
          // likes 배열에 새 like 객체 추가 (이미 존재하면 중복 방지)
          likes(existingLikes) {
            const arr = Array.isArray(existingLikes)
              ? [...existingLikes]
              : [];
            const alreadyExists = arr.some(
              (like: unknown) =>
                typeof like === "object" &&
                like !== null &&
                "__ref" in like &&
                (like as { __ref?: string }).__ref ===
                  `Like:${likeStoreData.id}`,
            );
            if (alreadyExists) return existingLikes;
            const newLikeRef = cache.writeFragment({
              data: likeStoreData,
              fragment: NEW_LIKE_FRAGMENT,
            });
            return [...arr, newLikeRef];
          },
        },
      });
    },
  });

  const [cancelLike] = useMutation(CANCEL_LIKE, {
    onError: (error) => console.error(error),
    update: (cache, result) => {
      const cancelLikeData = result.data?.cancelLike as
        | CancelLikeMutation["cancelLike"]
        | undefined;
      if (!cancelLikeData) return;
      cache.modify({
        id: `Store:${cancelLikeData.storeId}`,
        fields: {
          isLiked() {
            return false;
          },
          // likes 배열에서 해당 like 객체 삭제
          likes(existingLikes, { readField }) {
            const arr = Array.isArray(existingLikes) ? [...existingLikes] : [];
            return arr.filter(
              (likeRef: unknown) =>
                readField("id", likeRef as Reference) !== cancelLikeData.id,
            );
          },
        },
      });
      // Like 객체 자체도 캐시에서 제거
      cache.evict({ id: `Like:${cancelLikeData.id}` });
      cache.gc();
    },
  });

  return { likeStore, cancelLike };
}
